import React from 'react';
import { Alert, Box, Typography, Divider } from '@mui/material';
import { TrendingUp as MarkupIcon, LocalOffer as DiscountIcon } from '@mui/icons-material';
import { DecimalNumberInput, FlagSwitch } from '../../../../components/ui';
import AdjustmentTypeToggle from '../AdjustmentTypeToggle';
import * as S from '../../SalesStep2/SalesStep2.styles';

interface LinePricingToolbarProps {
  editable: boolean;
  /** False when every piece has catalog price 0 — increment/discount do not apply. */
  adjustmentsAllowed?: boolean;
  markupEnabled: boolean;
  allowIndividualMarkups: boolean;
  discountEnabled: boolean;
  allowIndividualDiscounts: boolean;
  markupType: 'PERCENTAGE' | 'AMOUNT';
  markupValue: number;
  discountType: 'PERCENTAGE' | 'AMOUNT';
  discountValue: number;
  maxUniformDiscountAmount: number;
  onMarkupToggle: (enabled: boolean) => void;
  onIndividualMarkupToggle: (enabled: boolean) => void;
  onMarkupTypeChange: (type: 'PERCENTAGE' | 'AMOUNT') => void;
  onMarkupValueChange: (value: number) => void;
  onDiscountToggle: (enabled: boolean) => void;
  onIndividualDiscountToggle: (enabled: boolean) => void;
  onDiscountTypeChange: (type: 'PERCENTAGE' | 'AMOUNT') => void;
  onDiscountValueChange: (value: number) => void;
}

const compactInputSx = {
  width: 96,
  '& .MuiInputBase-root': { fontSize: '0.8125rem' },
  '& .MuiFormHelperText-root': { display: 'none' },
};

const sectionHeaderSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  flexWrap: 'wrap' as const,
};

const uniformControlsSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  flexWrap: 'wrap' as const,
  pl: 3.5,
  pt: 0.5,
};

const LinePricingToolbar: React.FC<LinePricingToolbarProps> = ({
  editable,
  adjustmentsAllowed = true,
  markupEnabled,
  allowIndividualMarkups,
  discountEnabled,
  allowIndividualDiscounts,
  markupType,
  markupValue,
  discountType,
  discountValue,
  maxUniformDiscountAmount,
  onMarkupToggle,
  onIndividualMarkupToggle,
  onMarkupTypeChange,
  onMarkupValueChange,
  onDiscountToggle,
  onIndividualDiscountToggle,
  onDiscountTypeChange,
  onDiscountValueChange,
}) => {
  const markupIsPercentage = markupType === 'PERCENTAGE';
  const discountIsPercentage = discountType === 'PERCENTAGE';
  const controlsEnabled = editable && adjustmentsAllowed;
  const showMarkup = adjustmentsAllowed && markupEnabled;
  const showDiscount = adjustmentsAllowed && discountEnabled;

  return (
    <S.ToolbarPanel sx={{ mb: 1.5, p: 1.5 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {!adjustmentsAllowed && (
          <Alert severity="info" sx={{ py: 0.5, fontSize: '0.75rem' }}>
            No aplican incrementos ni descuentos: todas las piezas están en Bs 0.
          </Alert>
        )}

        <Box sx={sectionHeaderSx}>
          <MarkupIcon
            color={showMarkup ? 'success' : 'action'}
            sx={{ fontSize: 18, flexShrink: 0 }}
          />
          <Typography variant="body2" sx={{ fontWeight: 600, flexShrink: 0 }}>
            Incremento
          </Typography>
          <FlagSwitch
            checked={showMarkup}
            onChange={(e, checked) => onMarkupToggle(checked)}
            disabled={!controlsEnabled}
            size="small"
          />
          {showMarkup && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                Por pieza
              </Typography>
              <FlagSwitch
                checked={allowIndividualMarkups}
                onChange={(e, checked) => onIndividualMarkupToggle(checked)}
                disabled={!controlsEnabled}
                size="small"
              />
            </>
          )}
        </Box>

        {showMarkup && !allowIndividualMarkups && (
          <Box sx={uniformControlsSx}>
            <AdjustmentTypeToggle
              value={markupIsPercentage ? 'PERCENTAGE' : 'AMOUNT'}
              disabled={!controlsEnabled}
              onChange={onMarkupTypeChange}
              ariaLabel="Tipo de incremento"
            />
            <DecimalNumberInput
              size="small"
              value={markupValue > 0 ? markupValue : ''}
              onChange={(val) => onMarkupValueChange(val ?? 0)}
              decimalPlaces={2}
              min={0}
              disabled={!controlsEnabled}
              sx={compactInputSx}
            />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              {markupIsPercentage
                ? 'Porcentaje sobre cada pieza'
                : 'Monto repartido entre las piezas'}
            </Typography>
          </Box>
        )}

        <Divider sx={{ opacity: 0.6 }} />

        <Box sx={sectionHeaderSx}>
          <DiscountIcon
            color={showDiscount ? 'success' : 'action'}
            sx={{ fontSize: 18, flexShrink: 0 }}
          />
          <Typography variant="body2" sx={{ fontWeight: 600, flexShrink: 0 }}>
            Descuento
          </Typography>
          <FlagSwitch
            checked={showDiscount}
            onChange={(e, checked) => onDiscountToggle(checked)}
            disabled={!controlsEnabled}
            size="small"
          />
          {showDiscount && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                Por pieza
              </Typography>
              <FlagSwitch
                checked={allowIndividualDiscounts}
                onChange={(e, checked) => onIndividualDiscountToggle(checked)}
                disabled={!controlsEnabled}
                size="small"
              />
            </>
          )}
        </Box>

        {showDiscount && !allowIndividualDiscounts && (
          <Box sx={uniformControlsSx}>
            <AdjustmentTypeToggle
              value={discountIsPercentage ? 'PERCENTAGE' : 'AMOUNT'}
              disabled={!controlsEnabled}
              onChange={onDiscountTypeChange}
              ariaLabel="Tipo de descuento"
            />
            <DecimalNumberInput
              size="small"
              value={discountValue > 0 ? discountValue : ''}
              onChange={(val) => onDiscountValueChange(val ?? 0)}
              decimalPlaces={2}
              min={0}
              max={discountIsPercentage ? 99 : maxUniformDiscountAmount}
              disabled={!controlsEnabled}
              sx={compactInputSx}
            />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              {discountIsPercentage
                ? 'Porcentaje sobre cada pieza'
                : 'Monto repartido entre las piezas'}
            </Typography>
          </Box>
        )}
      </Box>
    </S.ToolbarPanel>
  );
};

export default LinePricingToolbar;
